import React, {useState} from 'react';
import {Avatar, Button, Dropdown, Layout, Menu} from 'antd';
import {LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, UserOutlined,} from '@ant-design/icons';
import {Outlet, useLocation, useNavigate} from 'react-router-dom';
import {useAppDispatch} from '../../store/hooks';
import {logout} from '../../store/slices/authSlice';
import {hasAnyPermission} from '@/utils/auth';
import type {MenuItem} from '../../config/menu.config';
import menuItems from '../../config/menu.config';
import styles from './MainLayout.module.css';

const { Header, Sider, Content, Footer } = Layout;

function allows(permission?: string | string[]): boolean {
  if (!permission) {
    return true;
  }
  const codes = Array.isArray(permission) ? permission : [permission];
  return hasAnyPermission(codes);
}

function filterMenuByPermission(items: MenuItem[]): MenuItem[] {
  return items
    .filter((item: MenuItem) => allows(item.requiredPermission))
    .map((item: MenuItem) => {
      if ('children' in item && Array.isArray(item.children)) {
        return {
          ...item,
          children: filterMenuByPermission(item.children as MenuItem[]),
        };
      }
      return item;
    })
    .filter((item: MenuItem) => {
      if ('children' in item && Array.isArray(item.children) && item.children.length === 0) {
        return false;
      }
      return true;
    });
}

function stripRequiredPermission(items: MenuItem[]): any[] {
  return items.map(({ requiredPermission, ...rest }) => {
    const newItem: any = { ...rest };
    if ('children' in rest && Array.isArray((rest as any).children)) {
      newItem.children = stripRequiredPermission((rest as any).children);
    }
    return newItem;
  });
}

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const handleMenuClick = (key: string) => {
    if (key.startsWith('/')) {
      navigate(key);
    }
  };

  const handleUserMenuClick = async ({ key }: { key: string }) => {
    if (key === 'logout') {
      await dispatch(logout());
      navigate('/login');
    } else if (key === 'profile') {
      navigate('/profile');
    }
  };

  const filteredMenuItems = filterMenuByPermission(menuItems);
  const menuItemsForAntd = stripRequiredPermission(filteredMenuItems);
  const username = localStorage.getItem('username') || '用户';

  return (
    <Layout className={styles.layout}>
      <Sider trigger={null} collapsible collapsed={collapsed}>
        <div className={styles.logo}>{!collapsed && <span>云租小栈-管理系统</span>}</div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItemsForAntd}
          onClick={({ key }) => handleMenuClick(key)}
        />
      </Sider>
      <Layout>
        <Header className={styles.header}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className={styles.trigger}
          />
          <div className={styles.userInfo}>
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick,
              }}
              placement="bottomRight"
            >
              <span className={styles.userDropdown}>
                <Avatar icon={<UserOutlined />} />
                <span className={styles.username}>{username}</span>
              </span>
            </Dropdown>
          </div>
        </Header>
        <Content className={styles.content}>
          <Outlet />
        </Content>
        <Footer className={styles.footer}>
          <div className={styles.footerContent}>
            <span className={styles.companyName}>淄博镜间摄影有限公司</span>
            <a
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.icpLink}
            >
              鲁ICP备2025164986号-1
            </a>
          </div>
        </Footer>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
